// =============================================================================
// src/features/briefing/briefing-route.ts — Rota fullscreen do Briefing do dia.
//
// Proveniencia: criado na reforma institucional 2026-06 (D2), clonando o
//   contrato da ExploreFullscreenRoute (explore-route.ts): body class propria,
//   fecha as rotas irmas + Cinema ao abrir, garante o mini-mapa em duplo rAF
//   (espera o layout fullscreen estabilizar) e re-sincroniza a topbar.
//   MAP_INVALIDATE_DELAY_MS 60 espelha o timing herdado do legado.
// =============================================================================

import { qs } from '../../app/dom';
import {
  getTopbarNav,
  getMapController,
  getExploreRouteGlobal,
  getGrafoRoute,
  getWorkspaceRoute,
} from '../explore/explore-globals';
import { BriefingMap } from './briefing-map';

/** Classe no <body> que ativa o layout fullscreen do Briefing. */
const BRIEFING_VIEW_CLASS = 'is-briefing-view';
/** Classe do Modo Cinema (removida ao abrir o Briefing). */
const CINEMA_ACTIVE_CLASS = 'cinema-active';
/** Ms ate re-medir o mapa principal apos fechar a rota (paridade explore-route). */
const MAP_INVALIDATE_DELAY_MS = 60;

/** Controlador da rota Briefing (open/close/init). Singleton exportado ao final. */
class BriefingFullscreenRoute {
  private page: HTMLElement | null = null;

  /** Resolve a pagina uma vez (mesmo contrato da ExploreRoute.init). */
  init(): void {
    this.page = qs<HTMLElement>('#u-briefing-page');
  }

  /** True quando o Briefing esta visivel. */
  isOpen(): boolean {
    return document.body.classList.contains(BRIEFING_VIEW_CLASS);
  }

  /** Abre a rota: fecha as irmas + Cinema, ativa o layout e garante o mini-mapa. */
  open(): void {
    if (this.isOpen()) return;
    this.closeSiblingRoutes();
    document.body.classList.remove(CINEMA_ACTIVE_CLASS);
    document.body.classList.add(BRIEFING_VIEW_CLASS);
    this.page?.setAttribute('aria-hidden', 'false');
    this.ensureMapAfterLayout();
    getTopbarNav()?.syncActive();
  }

  /** Fecha Explorar/Grafo/Workspace se estiverem abertas. */
  private closeSiblingRoutes(): void {
    const explore = getExploreRouteGlobal();
    if (explore?.isOpen()) explore.close();
    const grafo = getGrafoRoute();
    if (grafo?.isOpen?.()) grafo.close();
    const workspace = getWorkspaceRoute();
    if (workspace?.isOpen?.()) workspace.close();
  }

  /** Cria o mini-mapa em duplo rAF (layout fullscreen primeiro; paridade explore). */
  private ensureMapAfterLayout(): void {
    requestAnimationFrame(() => requestAnimationFrame(() => BriefingMap.ensure()));
  }

  /** Fecha a rota e re-mede o mapa principal apos a transicao. */
  close(): void {
    if (!this.isOpen()) return;
    document.body.classList.remove(BRIEFING_VIEW_CLASS);
    this.page?.setAttribute('aria-hidden', 'true');
    this.invalidateMainMap();
    getTopbarNav()?.syncActive();
  }

  /** Re-mede o mapa principal apos fechar (timing herdado: 60ms). */
  private invalidateMainMap(): void {
    const controller = getMapController();
    if (controller?.invalidateSize) setTimeout(() => controller.invalidateSize?.(), MAP_INVALIDATE_DELAY_MS);
  }
}

/** Singleton da rota Briefing — publicado em window.BriefingRoute por index.ts. */
export const BriefingRoute = new BriefingFullscreenRoute();
